import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CaretDownOutlined,
  CaretRightOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";
import HomeIcon from "@/assets/home.svg?react";
import AccountSettingsIcon from "@/assets/account_settings.svg?react";
import CredentialsIcon from "@/assets/credentials.svg?react";
import EventIcon from "@/assets/event.svg?react";
import PermissionsIcon from "@/assets/permissions.svg?react";
import SecurityIcon from "@/assets/security.svg?react";
import VersionsIcon from "@/assets/versions.svg?react";
// import SubscriptionsIcon from "@/assets/subscription.svg?react";
import homeBg from "@/assets/images/home_bg.png";
import { App, Dropdown } from "antd";
import type { MenuProps } from "antd";
import { listAllVersions } from "@/api/versions";
import { isAppIconSrc } from "@/lib/appIcon";
import { isMockSpaceId } from "@/lib/currentOrg";
import { useAppStore } from "@/store";
import { useUserStore, useVersionDrawerStore } from "@/store";
import { useShallow } from "zustand/react/shallow";
import type { AppInfo, AppVersion, Collaborator } from "@/types";
import { platformLabel } from "@/pages/app-detail/versions/platform";
import { versionPlatformEntries } from "@/pages/app-detail/versions/platformEntries";
import { pickSidebarVersions } from "@/pages/app-detail/versions/sidebarSlots";
import { StatusTag } from "@/pages/app-detail/versions/StatusTag";
import {
  confirmVersionAction,
  isDangerAction,
  siderMenuActionsForStatus,
  type VersionListAction,
} from "@/pages/app-detail/versions/versionActions";

const EMPTY_VERSIONS: AppVersion[] = [];
const EMPTY_COLLABORATORS: Collaborator[] = [];

const OTHER_NAV: {
  key: string;
  labelKey: string;
  icon: ReactNode;
}[] = [
  // {
  //   key: "products",
  //   labelKey: "appNav.products",
  //   icon: <SubscriptionsIcon className="size-6" />,
  // },
  {
    key: "credentials",
    labelKey: "appNav.credentials",
    icon: <CredentialsIcon className="size-6" />,
  },
  {
    key: "scope",
    labelKey: "appNav.scope",
    icon: <PermissionsIcon className="size-6" />,
  },
  {
    key: "events",
    labelKey: "appNav.events",
    icon: <EventIcon className="size-6" />,
  },
  {
    key: "security",
    labelKey: "appNav.security",
    icon: <SecurityIcon className="size-6" />,
  },
  {
    key: "account",
    labelKey: "appNav.accountSettings",
    icon: <AccountSettingsIcon className="size-6" />,
  },
];

function parseSelection(pathname: string) {
  const m = pathname.match(/\/apps\/[^/]+\/(.+)$/);
  const rest = m?.[1] ?? "credentials";

  if (rest === "versions") {
    return { kind: "all-versions" as const };
  }
  if (rest === "versions/new") {
    return { kind: "new-version" as const };
  }
  const detail = rest.match(/^versions\/([^/]+)$/);
  if (detail) {
    return {
      kind: "version" as const,
      version_id: detail[1],
    };
  }
  return { kind: "nav" as const, key: rest.split("/")[0] ?? rest };
}

function NavRow({
  active,
  icon,
  label,
  badge,
  onClick,
  extra,
}: {
  active?: boolean;
  icon?: ReactNode;
  label: ReactNode;
  badge?: ReactNode;
  onClick?: () => void;
  extra?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex h-12 w-full cursor-pointer items-center gap-2 rounded-lg border-none px-3 py-2 text-left transition-colors ${
        active ? "bg-fill" : "bg-transparent hover:bg-fill-hover"
      }`}
    >
      {icon && (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-base text-primary">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-sm text-primary">
        {label}
      </span>
      {badge}
      {extra}
    </button>
  );
}

function VersionNode({
  app_id,
  version,
  selected,
  expanded,
  onToggleExpand,
  onOpen,
  isOwner,
  storeActions,
  appVersions,
}: {
  app_id: string;
  version: AppVersion;
  appVersions: AppVersion[];
  selected: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpen: () => void;
  isOwner: boolean;
  storeActions: {
    deleteVersion: (app_id: string, version_id: string) => void;
    withdrawVersion: (app_id: string, version_id: string) => void;
    cancelSchedule: (app_id: string, version_id: string) => void;
    releaseNow: (app_id: string, version_id: string) => void;
    delistVersion: (app_id: string, version_id: string) => void;
    disableVersion: (app_id: string, version_id: string) => void;
    enableVersion: (app_id: string, version_id: string) => void;
    relistVersion: (app_id: string, version_id: string) => void;
    rollbackVersion: (app_id: string, version_id: string) => void;
  };
}) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const menuActions = isOwner ? siderMenuActionsForStatus(version.status) : [];

  const menuItems: MenuProps["items"] = menuActions.map((action) => ({
    key: action,
    danger: isDangerAction(action),
    label: t(`versions.action_${action}` as const),
    onClick: ({ domEvent }) => {
      domEvent.stopPropagation();
      confirmVersionAction({
        t,
        modal,
        message,
        action: action as VersionListAction,
        version,
        app_id,
        store: storeActions,
        appVersions,
      });
    },
  }));
  const platformEntries = versionPlatformEntries(version);

  const goDetail = () => {
    onOpen();
    if (!expanded) onToggleExpand();
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={goDetail}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") goDetail();
        }}
        className={`group flex w-full cursor-pointer items-center gap-1 rounded-lg px-2 py-1.5 ${
          selected ? "bg-fill" : "bg-transparent"
        }`}
      >
        <button
          type="button"
          aria-label={expanded ? "collapse" : "expand"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          className="flex size-5 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-muted"
        >
          {expanded ? (
            <CaretDownOutlined className="text-[10px]" />
          ) : (
            <CaretRightOutlined className="text-[10px]" />
          )}
        </button>
        <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-[13px]">
          <span className="text-primary">v{version.version}</span>
          <StatusTag status={version.status} compact />
        </span>
        {menuItems.length > 0 && (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <button
              type="button"
              className="cursor-pointer rounded-md border-none bg-transparent p-1 opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <EllipsisOutlined />
            </button>
          </Dropdown>
        )}
      </div>
      {expanded && (
        <div className="relative pl-[34px]">
          <div className="absolute bottom-2 left-[23px] top-0 w-px bg-line" />
          {platformEntries.map(({ platform, reusedFrom }) => (
            <button
              key={platform}
              type="button"
              onClick={onOpen}
              className="flex w-full cursor-pointer items-center gap-1.5 rounded border-none bg-transparent py-1.5 pl-2 text-left text-[13px] text-secondary"
            >
              {platformLabel(platform)}
              {reusedFrom && (
                <span className="text-xs text-muted">
                  {t("versions.reusedFrom", { version: reusedFrom })}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppDetailSider({ app }: { app: AppInfo }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { app_id = app.id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserStore((s) => s.user);
  const { openVersion, drawerVersionId, drawerOpen } = useVersionDrawerStore(
    useShallow((s) => ({
      openVersion: s.openVersion,
      drawerVersionId: s.version_id,
      drawerOpen: s.open,
    })),
  );
  const versions = useAppStore((s) => s.versions[app.id] ?? EMPTY_VERSIONS);
  const versionsListRev = useAppStore((s) => s.versionsListRev);
  const syncVersions = useAppStore((s) => s.syncVersions);
  const setMaxOnlineVersion = useAppStore((s) => s.setMaxOnlineVersion);
  const spacesLoad = useUserStore((s) => s.spacesLoad);
  const spaceId = useUserStore((s) => s.space_id);
  const spaceReady =
    spacesLoad === "ready" && Boolean(spaceId) && !isMockSpaceId(spaceId);
  const collaborators = useAppStore(
    (s) => s.collaborators[app.id] ?? EMPTY_COLLABORATORS,
  );
  const storeActions = useAppStore(
    useShallow((s) => ({
      deleteVersion: s.deleteVersion,
      withdrawVersion: s.withdrawVersion,
      cancelSchedule: s.cancelSchedule,
      releaseNow: s.releaseNow,
      delistVersion: s.delistVersion,
      disableVersion: s.disableVersion,
      enableVersion: s.enableVersion,
      relistVersion: s.relistVersion,
      rollbackVersion: s.rollbackVersion,
    })),
  );

  useEffect(() => {
    if (!spaceReady) return;
    let cancelled = false;
    void listAllVersions(app.id)
      .then((res) => {
        if (!cancelled) {
          syncVersions(app.id, res.list, { replace: true });
          setMaxOnlineVersion(app.id, res.max_online_version);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        message.error(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
    // 版本写入会 bump。丢掉这次还在飞的全量结果，再拉一次，避免旧列表整表替换掉新草稿。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.id, spaceReady, versionsListRev]);

  const selection = parseSelection(location.pathname);
  const selectedVersionId = drawerOpen ? drawerVersionId : null;
  const sidebarVersions = useMemo(
    () => pickSidebarVersions(versions),
    [versions],
  );
  const moduleId = app.module_id || app.app_id;
  const isOwner = collaborators.some(
    (c) => c.role === "owner" && c.id === user?.id,
  );

  /** 手风琴：同时只展开一个版本 */
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(
    null,
  );

  // 进入某版本详情时自动展开对应节点（不覆盖用户手动收起：仅在 version_id 变化时同步）
  useEffect(() => {
    if (selectedVersionId) {
      setExpandedVersionId(selectedVersionId);
    }
  }, [selectedVersionId]);

  const toggleExpand = (version_id: string) => {
    setExpandedVersionId((prev) => (prev === version_id ? null : version_id));
  };

  return (
    <aside className="flex w-sidebar shrink-0 flex-col self-stretch overflow-hidden border-r border-line bg-surface">
      <Link
        to="/apps"
        className="flex h-12 shrink-0 items-center gap-2 border-b border-line !bg-surface-muted px-[22px] no-underline"
      >
        <span className="flex size-6 items-center justify-center">
          <HomeIcon className="size-6" />
        </span>
        <span className="text-sm font-medium text-primary">
          {t("appNav.backToHome")}
        </span>
      </Link>

      <div className="relative flex h-40 shrink-0 flex-col items-center justify-center gap-2 overflow-hidden border-b border-line">
        <img
          src={homeBg}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 size-full object-cover"
        />
        <div className="relative flex size-[54px] items-center justify-center overflow-hidden rounded-[14px] bg-surface text-[28px] shadow-icon">
          {isAppIconSrc(app.icon) ? (
            <img src={app.icon} alt="" className="size-full object-cover" />
          ) : (
            app.icon
          )}
        </div>
        <div className="relative w-full min-w-0 px-4 text-center">
          <span
            title={app.name}
            className="block truncate text-lg font-medium text-primary"
          >
            {app.name}
          </span>
          <span
            title={moduleId}
            className="block truncate text-[13px] text-secondary"
          >
            {t("appNav.moduleId")}: {moduleId}
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-2.5">
        <div>
          <NavRow
            active={selection.kind === "all-versions"}
            icon={<VersionsIcon className="size-6" />}
            label={
              <span className="font-medium">{t("appNav.allVersions")}</span>
            }
            badge={
              <span className="rounded-full bg-fill-strong px-2 text-xs leading-[18px]">
                {versions.length}
              </span>
            }
            onClick={() => navigate(`/apps/${app_id}/versions`)}
          />

          <div className="mt-1 flex flex-col gap-1">
            {sidebarVersions.map((v) => (
              <VersionNode
                key={v.id}
                app_id={app_id}
                version={v}
                selected={selectedVersionId === v.id}
                expanded={expandedVersionId === v.id}
                onToggleExpand={() => toggleExpand(v.id)}
                onOpen={() => openVersion(app_id, v.id)}
                isOwner={isOwner}
                storeActions={storeActions}
                appVersions={versions}
              />
            ))}
          </div>
        </div>

        <div className="mt-1 flex flex-col gap-0.5">
          {OTHER_NAV.map((item) => (
            <NavRow
              key={item.key}
              active={selection.kind === "nav" && selection.key === item.key}
              icon={item.icon}
              label={t(item.labelKey as "appNav.credentials")}
              onClick={() => navigate(`/apps/${app_id}/${item.key}`)}
            />
          ))}
        </div>
      </div>
    </aside>
  );
}
